import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { FinanceApiService, SemanticMetricRecord } from './finance-api.service';

@Component({
  standalone: true,
  selector: 'ain-finance-semantic-metrics',
  imports: [AinPageComponent, AinStateComponent, FormsModule, TableModule, ButtonModule, InputTextModule],
  providers: [MessageService],
  template: `
    <ain-page title="Semantic Metrics" description="Create and publish metric definitions used by finance analytics">
      @if (!capabilityEnabled()) {
        <ain-state type="empty" title="Semantic layer capability is disabled" message="Enable financial-core.semantic-layer in control center." />
      } @else {
      <div class="toolbar">
        <input pInputText [(ngModel)]="metricKey" placeholder="metric key" />
        <input pInputText [(ngModel)]="versionText" placeholder="version" />
        <input pInputText [(ngModel)]="formulaJson" placeholder='{"dsl":"sum(revenue)-sum(expense)"}' />
        <button pButton type="button" label="Create" (click)="create()"></button>
      </div>

      <p-table [value]="metrics()">
        <ng-template pTemplate="header">
          <tr><th>Key</th><th>Version</th><th>Published</th><th>Action</th></tr>
        </ng-template>
        <ng-template pTemplate="body" let-row>
          <tr>
            <td>{{ row.metricKey }}</td>
            <td>{{ row.version }}</td>
            <td>{{ row.isPublished ? 'Yes' : 'No' }}</td>
            <td>
              <button pButton type="button" size="small" [disabled]="row.isPublished" label="Publish" (click)="publish(row.id)"></button>
            </td>
          </tr>
        </ng-template>
      </p-table>
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { display: flex; gap: 8px; margin-bottom: 12px; flex-wrap: wrap; }
  `]
})
export class FinanceSemanticMetricsComponent implements OnInit {
  private readonly api = inject(FinanceApiService);
  private readonly messages = inject(MessageService);

  readonly metrics = signal<SemanticMetricRecord[]>([]);
  readonly capabilityEnabled = signal(false);
  metricKey = '';
  versionText = '1';
  formulaJson = '{"dsl":"sum(revenue)-sum(expense)"}';

  ngOnInit(): void {
    this.api.getCapabilitySetting('financial-core.semantic-layer').subscribe({
      next: setting => {
        this.capabilityEnabled.set(setting.isEnabled);
        if (setting.isEnabled) this.load();
      }
    });
  }

  load(): void {
    this.api.listSemanticMetrics().subscribe({ next: rows => this.metrics.set(rows) });
  }

  create(): void {
    const version = Number(this.versionText);
    if (!this.metricKey.trim() || Number.isNaN(version) || !this.formulaJson.trim()) return;
    this.api.createSemanticMetric({
      metricKey: this.metricKey.trim(),
      version,
      formulaJson: this.formulaJson.trim()
    }).subscribe({
      next: () => {
        this.metricKey = '';
        this.load();
      },
      error: () => this.messages.add({ severity: 'error', summary: 'Create semantic metric failed' })
    });
  }

  publish(metricId: string): void {
    this.api.publishSemanticMetric(metricId).subscribe({
      next: () => this.load(),
      error: () => this.messages.add({ severity: 'error', summary: 'Publish semantic metric failed' })
    });
  }
}
